/**
 * SW2-2230 — the one place that answers "is this positionable a legend item?".
 *
 * `positionable_type` has no single spelling on the frontend, and that ambiguity has caused two
 * separate bugs, so it is worth stating plainly:
 *
 *  - the **server** stores and returns `legend_items`. That is the value in the `positionables`
 *    table, and the value the offline download writes to IndexedDB.
 *  - the **map editor** treats each legend subtype as its own data source, so
 *    `PositionableDataSource` overwrites `positionable_type` with the subtype
 *    (`control_measure` / `pollutant`) after fetching, and stashes the server's value on
 *    `_context`. See the `positionableContext` prop docs in `DataSource.js`.
 *
 * A row or feature can therefore carry either spelling depending on which layer produced it, and
 * anything that hard-codes one is wrong for the other half of the app. Two symptoms this caused:
 * the offline finding picker filtered on `=== 'legend_items'` and matched nothing, and the map
 * context menu's read-only check looked up a data source by the wrong name and returned "not read
 * only" for every legend item.
 *
 * Persisted rows should go through `toCanonicalPositionableType` so the cache converges on the
 * server's spelling; code that merely *tests* a value should use `isLegendItemType` and accept any
 * of them.
 */

/** Every spelling of "legend item" that can appear on a positionables row or a map feature. */
export const LEGEND_ITEM_TYPES = ['legend_items', 'control_measure', 'pollutant'];

/** The server's spelling — what belongs in the database and in the offline cache. */
export const CANONICAL_LEGEND_ITEM_TYPE = 'legend_items';

export const isLegendItemType = (type) => LEGEND_ITEM_TYPES.includes(type);

/**
 * Normalise a `positionable_type` before persisting it.
 *
 * Non-legend types (findings, commentables, …) are returned untouched — this only collapses the
 * legend subtypes back onto the server's spelling.
 */
export const toCanonicalPositionableType = (type) =>
  isLegendItemType(type) ? CANONICAL_LEGEND_ITEM_TYPE : type;
